import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Form, FormGroup } from 'semantic-ui-react';
import {
  ControlledTextFormField,
  DropdownFormField,
  ShadowBox,
  SliderFormField,
} from '../../../../elements';
import { CommonUtility, ToastMessage } from '../../../../utility';
import { WarehouseEmailHook } from '../../../../hooks';
import { WarehouseService } from '../../../../utility/services/warehouse';
import { AlertFooterBar } from '../../../../elements/FooterAlert';

const ItemSchema = yup.object().shape({
  emailList: yup.array(),
  email: yup.boolean(),
  generatePackingList: yup.boolean(),
  type: yup.string(),
  spreadsheetId: yup.string(),
  sheetName: yup.string(),
  firstRow: yup
    .number()
    .integer('First row must be an integer')
    .nullable()
    .typeError('First row must be an integer'),
});

export function EmailPickListTab({ warehouseId }) {
  const { data, loading, setData } = WarehouseEmailHook(warehouseId);

  const [emails, setEmails] = useState([]);

  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    reset,
    setError,
    clearErrors,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(ItemSchema),
    shouldUnregister: true,
    mode: 'onChange',
  });

  const type = watch('type');

  const isSheet = useMemo(() => type === 'sheets', [type]);

  useEffect(() => {
    if (CommonUtility.isValidObject(data)) {
      setEmails(
        data.emailList?.map(email => ({
          value: email,
          key: email,
          text: email,
        })) || [],
      );
      resetForm();
    }
  }, [data]);

  const [updating, setUpdating] = useState(false);

  const updateEmails = async formValues => {
    try {
      setUpdating(true);
      const payload = {
        ...formValues,
      };

      if (!isSheet) {
        delete payload.spreadsheetId;
        delete payload.sheetName;
        delete payload.firstRow;
      }

      const res = await WarehouseService.updateWarehouseEmail(warehouseId, payload);
      ToastMessage.success(`E-mail Packlists updated successfully`);
      setData(res);
    } catch (error) {
      ToastMessage.apiError(error?.error || error);
    } finally {
      setUpdating(false);
    }
  };

  const resetForm = () => {
    reset({
      email: data?.email,
      generatePackingList: data?.generatePackingList,
      emailList: data?.emailList || '',
      type: data?.type,
      spreadsheetId: data?.spreadsheetId,
      sheetName: data?.sheetName,
      firstRow: data?.firstRow,
    });
  };

  function isValidEmail(email) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }

  const onAddEmail = (e, { value }) => {
    if (emails.find(x => x.value === value)) {
      return;
    }

    if (!isValidEmail(value)) {
      setError('emailList', { message: 'Please enter valid email address' });
      return;
    }

    clearErrors('emailList');

    setEmails([
      ...emails,
      {
        text: value,
        key: value,
        value,
      },
    ]);
  };

  return (
    <div className="pt-4">
      <ShadowBox>
        <Form className="d-flex justify-content-between align-items-start">
          <div className="d-flex flex-column align-items-start mt-4 pt-2">
            <div className="d-flex align-items-start mb-3">
              <SliderFormField
                control={control}
                name="email"
                error={errors.email}
                hint={errors?.email?.message}
                fieldClass="mb-0"
              />
              <h3 className="mt-1 text-nowrap mr-4">Receive PackLists by Email</h3>
            </div>
            <div className="d-flex align-items-start">
              <SliderFormField
                control={control}
                name="generatePackingList"
                error={errors.generatePackingList}
                hint={errors?.generatePackingList?.message}
                fieldClass="mb-0"
              />
              <h3 className="mt-1 text-nowrap mr-4">Generate Packing List on Labels</h3>
            </div>
          </div>
          <div className="w-50">
            <FormGroup widths="equal" className="mb-4">
              <DropdownFormField
                control={control}
                name="emailList"
                error={errors.emailList}
                hint={errors?.emailList?.message}
                label="Attention"
                options={emails}
                multiple
                loading={loading}
                fieldClass="mb-0"
                allowAdditions
                onAddItem={onAddEmail}
                onChange={(e, { value }) => {
                  const validEmails = value.filter(isValidEmail);
                  if (validEmails.length !== value.length) {
                    setError('emailList', {
                      message: 'Only valid emails are allowed',
                    });
                  } else {
                    clearErrors('emailList');
                  }
                  // Update react-hook-form manually if needed
                  setValue('emailList', validEmails, { shouldDirty: true });
                }}
              />
              <DropdownFormField
                control={control}
                fieldClass="mb-0"
                loading={loading}
                name="type"
                error={errors.type}
                hint={errors?.type?.message}
                label="Type"
                options={[
                  {
                    text: 'Sheets',
                    value: 'sheets',
                    key: 'sheets',
                  },
                  {
                    text: 'Csv',
                    value: 'csv',
                    key: 'csv',
                  },
                ]}
              />
            </FormGroup>
            {isSheet && (
              <>
                <b>Google Sheet Configuration</b>
                <FormGroup widths="equal" className="mt-1">
                  <ControlledTextFormField
                    control={control}
                    loading={loading}
                    name="spreadsheetId"
                    error={errors.spreadsheetId}
                    hint={errors?.spreadsheetId?.message}
                    label="Spreadsheet ID"
                  />
                  <ControlledTextFormField
                    control={control}
                    loading={loading}
                    name="sheetName"
                    error={errors.sheetName}
                    hint={errors?.sheetName?.message}
                    label="Sheet Name"
                  />
                  <ControlledTextFormField
                    control={control}
                    loading={loading}
                    name="firstRow"
                    error={errors.firstRow}
                    hint={errors?.firstRow?.message}
                    label="Starting Row"
                  />
                </FormGroup>
              </>
            )}
          </div>
        </Form>
        {CommonUtility.isValidObject(dirtyFields) && (
          <AlertFooterBar
            cancel={resetForm}
            save={handleSubmit(updateEmails)}
            loading={updating}
          />
        )}
      </ShadowBox>
    </div>
  );
}
